import './styles.css';

import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { UnicastFormValues } from 'components/Graph/GraphSidePanel/GraphPathlookup/UnicastFormValues';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { type FC, useState } from 'react';
import { isValidIpv4or6Address } from 'utils/formValidation/validations';
import { useApi } from 'utils/request/useApi';
import { GraphUnicastForm } from './GraphUnicastForm';
import { PathCheck } from './PathCheck';
import { getPathLookupUnicastFromLocalStorage } from './PathLookupLocalStorage';
import { type Vrf, fetchVrf } from './fetchVrf';
import { getVrfOption } from './helpers/getVrfOption';
import { useGraphPathlookupForm } from './useGraphPathlookupForm';

interface Props {
  resetAdvancedOptionsToSelectedPreset: () => void;
  formik: FormikProps<UnicastFormValues>;
}

export const GraphUnicast: FC<Props> = (props) => {
  const msg = useLocale();
  const apiRequest = useApi().request;
  const ctxProps = useGraphContextSelectors({
    snapshotId: prop('snapshotId'),
  });
  const [vrf, setVrf] = useState<Vrf[]>([]);

  const localStoragePathlookupUnicast = getPathLookupUnicastFromLocalStorage();

  const handleSwitchValues = useStableCallback(async (switchPorts: boolean) => {
    props.formik.setFieldValue(
      'startingPoint',
      props.formik.values.destinationPoint,
    );
    props.formik.setFieldValue(
      'destinationPoint',
      props.formik.values.startingPoint,
    );
    props.formik.setFieldValue('srcRegions', props.formik.values.dstRegions);
    props.formik.setFieldValue('dstRegions', props.formik.values.srcRegions);
    if (switchPorts) {
      props.formik.setFieldValue('srcPorts', props.formik.values.dstPorts);
      props.formik.setFieldValue('dstPorts', props.formik.values.srcPorts);
    }
    if (isValidIpv4or6Address(props.formik.values.destinationPoint)) {
      const vrfResponse = await fetchVrf(
        apiRequest,
        ctxProps.snapshotId,
        props.formik.values.destinationPoint,
      );
      setVrf(vrfResponse);
      props.formik.setFieldValue(
        'vrf',
        // Fixed noUncheckedIndexedAccess NIM-19408
        vrf.length ? getVrfOption(vrf[0]!, msg).value : null,
      );
    }
  });

  const { handleProtocolChange } = useGraphPathlookupForm({
    loadedFromLocalStorage: localStoragePathlookupUnicast !== null,
    updateFieldValue: props.formik.setFieldValue,
    updateVrf: setVrf,
    vrf,
    vrfValue: props.formik.values.vrf,
    vrfIpError: props.formik.errors.startingPoint,
    vrfIpValue: props.formik.values.startingPoint,
  });

  return (
    <ScrollColumnView className='graph-pathlookup__content'>
      <GraphUnicastForm
        resetAdvancedOptionsToSelectedPreset={
          props.resetAdvancedOptionsToSelectedPreset
        }
        formik={props.formik}
        onProtocolChange={handleProtocolChange}
        onSwitchValues={handleSwitchValues}
        vrf={vrf}
      />
      <PathCheck type='unicast' />
    </ScrollColumnView>
  );
};
